import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { AlertTriangle, Mic, Pill } from "lucide-react";
import { useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../components/ui/Button.tsx";
import { HttpError, clinical } from "../../lib/api.ts";
import { notifyError, notifySuccess } from "../../lib/toast.ts";
import { useDialog } from "../../lib/useDialog.ts";
import {
	type PrescriptionDraftItem,
	useAssistantDraftStore,
} from "../../stores/assistant-draft-store.ts";

/**
 * Reviews + confirms a prescription the doctor dictated by voice. The draft is
 * editable (the doctor MUST be able to fix a misheard dose) and nothing is
 * written until they press Confirm — which runs through the normal create-
 * prescription path (allergy + interaction checks included). Rendered at the
 * app root so it survives the assistant panel closing.
 */
export function PrescriptionDraftModal() {
	const draft = useAssistantDraftStore((s) => s.prescription);
	if (!draft) return null;
	// key forces a fresh editable copy whenever a new draft arrives.
	return <DraftBody key={draft.patient_id + draft.dictation} />;
}

function DraftBody() {
	const { t } = useTranslation("assistant");
	const navigate = useNavigate();
	const draft = useAssistantDraftStore((s) => s.prescription);
	const clear = useAssistantDraftStore((s) => s.clearPrescriptionDraft);
	const [items, setItems] = useState<PrescriptionDraftItem[]>(
		() => draft?.items.map((i) => ({ ...i })) ?? [],
	);
	const [override, setOverride] = useState(false);
	// Server-side safety gate (422) override for the voice path — mirrors the
	// full prescription form so a dictated Rx that trips an interaction / allergy
	// / teratogen block isn't a dead end (self-review regression).
	const [safetyBlock, setSafetyBlock] = useState<string | null>(null);
	const [safetyAck, setSafetyAck] = useState(false);
	// a11y: focus-trap + Escape-to-close + scroll-lock on this bespoke overlay
	// (audit: it lacked dialog semantics — and it's the doctor's dose-correction
	// screen, where a keyboard/screen-reader user must not lose focus behind it).
	const dialogRef = useRef<HTMLDivElement>(null);
	const titleId = useId();
	useDialog(dialogRef, clear, true);

	const mutation = useMutation({
		mutationFn: () => {
			if (!draft) throw new Error("no draft");
			return clinical.createPrescription({
				patient_id: draft.patient_id,
				items: items.map((i) => ({
					medication_name: i.medication_name.trim(),
					dosage: i.dosage.trim(),
					frequency: i.frequency.trim(),
					...(i.duration_days ? { duration_days: i.duration_days } : {}),
					...(i.instructions?.trim()
						? { instructions: i.instructions.trim() }
						: {}),
				})),
				...(override ? { allergy_unrecorded_override: true } : {}),
				...(safetyAck
					? {
							allergy_override: true,
							interaction_override: true,
							pregnancy_override: true,
						}
					: {}),
			});
		},
		onSuccess: (rx) => {
			notifySuccess(t("draft.issued"));
			const patientId = draft?.patient_id;
			clear();
			if (patientId) {
				navigate({
					to: "/patients/$patientId",
					params: { patientId },
				});
			}
			return rx;
		},
		onError: (err) => {
			// A deterministic safety block (422) is surfaced as an in-modal warning
			// the doctor can consciously override — not a dead-end toast.
			if (
				err instanceof HttpError &&
				err.status === 422 &&
				/محظورة لأسباب سلامة|حساسية المريض غير/.test(err.error.message)
			) {
				setSafetyBlock(err.error.message);
				return;
			}
			notifyError(err);
		},
	});

	if (!draft) return null;

	const isUnrecorded = draft.allergy_status === "unrecorded";
	const hasItems = items.length > 0;
	// Server enforces completeness; mirror it so the button is honest.
	const allRowsComplete = items.every(
		(i) => i.medication_name.trim() && i.dosage.trim() && i.frequency.trim(),
	);
	const nkdaBlocked = isUnrecorded && !override;
	const httpErr = mutation.error instanceof HttpError ? mutation.error : null;

	const setItem = (idx: number, patch: Partial<PrescriptionDraftItem>) =>
		setItems((prev) =>
			prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)),
		);
	const removeItem = (idx: number) =>
		setItems((prev) => prev.filter((_, i) => i !== idx));

	return (
		<div
			className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4"
			onMouseDown={(e) => {
				if (e.target === e.currentTarget) clear();
			}}
		>
			<div
				ref={dialogRef}
				// biome-ignore lint/a11y/useSemanticElements: bespoke dose-review dialog with a managed focus trap (useDialog) + bottom-sheet layout; native <dialog> would override the managed behavior
				role="dialog"
				aria-modal="true"
				aria-labelledby={titleId}
				tabIndex={-1}
				className="w-full sm:max-w-2xl max-h-[92dvh] bg-card rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden outline-none"
			>
				{/* Header */}
				<div className="flex items-center gap-3 px-4 py-3 border-b border-border">
					<div className="size-9 rounded-full bg-gradient-brand flex items-center justify-center text-white shrink-0">
						<Pill className="size-5" strokeWidth={2} aria-hidden />
					</div>
					<div className="min-w-0">
						<h3 id={titleId} className="font-bold leading-tight">
							{t("draft.title")}
						</h3>
						<p className="text-xs text-muted-foreground leading-tight truncate">
							{draft.patient_name}
						</p>
					</div>
				</div>

				<div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
					{/* Safety banner */}
					<div className="rounded-lg bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900 px-3 py-2 text-xs text-amber-800 dark:text-amber-300 flex gap-2">
						<AlertTriangle className="size-4 shrink-0 mt-0.5" aria-hidden />
						<span>{t("draft.safety")}</span>
					</div>

					{/* What was heard */}
					<div className="rounded-lg bg-muted border border-border px-3 py-2">
						<div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-1">
							<Mic className="size-3.5" aria-hidden /> {t("draft.heard")}
						</div>
						<p className="text-sm whitespace-pre-wrap">{draft.dictation}</p>
					</div>

					{/* Empty draft → guidance, no confirm */}
					{!hasItems && (
						<div className="rounded-lg bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-900 px-3 py-3 text-sm text-blue-800 dark:text-blue-300">
							{draft.reason || t("draft.empty")}
						</div>
					)}

					{/* Editable items */}
					{hasItems &&
						items.map((item, idx) => (
							<div
								// biome-ignore lint/suspicious/noArrayIndexKey: fixed-length editable draft, rows aren't reordered
								key={idx}
								className="rounded-xl border border-border p-3 space-y-2"
							>
								<div className="flex items-center justify-between gap-2">
									<span className="text-xs font-semibold text-muted-foreground">
										{t("draft.medication")} {idx + 1}
									</span>
									{items.length > 1 && (
										<button
											type="button"
											onClick={() => removeItem(idx)}
											className="text-xs text-red-600 hover:underline"
										>
											{t("draft.remove")}
										</button>
									)}
								</div>
								<input
									value={item.medication_name}
									onChange={(e) =>
										setItem(idx, { medication_name: e.target.value })
									}
									placeholder={t("draft.medication")}
									className="w-full rounded-lg border border-border bg-card px-3 py-2 text-base sm:text-sm font-medium focus:outline-none focus:ring-2 focus:ring-brand-500"
								/>
								<div className="grid grid-cols-2 gap-2">
									<input
										value={item.dosage}
										onChange={(e) => setItem(idx, { dosage: e.target.value })}
										placeholder={t("draft.dosage")}
										className="w-full rounded-lg border border-border bg-card px-3 py-2 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
									/>
									<input
										value={item.frequency}
										onChange={(e) =>
											setItem(idx, { frequency: e.target.value })
										}
										placeholder={t("draft.frequency")}
										className="w-full rounded-lg border border-border bg-card px-3 py-2 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
									/>
								</div>
								<div className="grid grid-cols-3 gap-2">
									<input
										type="number"
										min={1}
										max={365}
										value={item.duration_days ?? ""}
										onChange={(e) =>
											setItem(idx, {
												duration_days: e.target.value
													? Number(e.target.value)
													: undefined,
											})
										}
										placeholder={t("draft.duration")}
										className="w-full rounded-lg border border-border bg-card px-3 py-2 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
									/>
									<input
										value={item.instructions ?? ""}
										onChange={(e) =>
											setItem(idx, { instructions: e.target.value })
										}
										placeholder={t("draft.instructions")}
										className="col-span-2 w-full rounded-lg border border-border bg-card px-3 py-2 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
									/>
								</div>
							</div>
						))}

					{/* NKDA gate */}
					{hasItems && isUnrecorded && (
						<label className="flex items-start gap-2 rounded-lg bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 px-3 py-2 text-xs text-red-800 dark:text-red-300 cursor-pointer">
							<input
								type="checkbox"
								checked={override}
								onChange={(e) => setOverride(e.target.checked)}
								className="mt-0.5"
							/>
							<span>{t("draft.nkda")}</span>
						</label>
					)}

					{httpErr && !safetyBlock && (
						<div className="rounded-lg bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 px-3 py-2 text-sm text-red-700 dark:text-red-400">
							{httpErr.error.message || t("draft.error")}
						</div>
					)}

					{safetyBlock && (
						<div className="rounded-lg bg-red-50 dark:bg-red-950/40 border border-red-300 dark:border-red-800 px-3 py-2 text-sm text-red-800 dark:text-red-300">
							<p className="font-semibold leading-snug">{safetyBlock}</p>
							<label className="mt-2 flex items-start gap-2 cursor-pointer">
								<input
									type="checkbox"
									checked={safetyAck}
									onChange={(e) => setSafetyAck(e.target.checked)}
									className="mt-0.5"
								/>
								<span>أؤكّد المتابعة على مسؤوليتي رغم التحذير.</span>
							</label>
						</div>
					)}
				</div>

				{/* Footer */}
				<div className="border-t border-border p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] flex items-center justify-end gap-2">
					<Button variant="ghost" onClick={clear} disabled={mutation.isPending}>
						{t("draft.cancel")}
					</Button>
					{hasItems && (
						<Button
							onClick={() => mutation.mutate()}
							loading={mutation.isPending}
							disabled={
								!allRowsComplete || nkdaBlocked || (!!safetyBlock && !safetyAck)
							}
							variant={safetyBlock ? "danger" : "primary"}
						>
							{safetyBlock ? "إصدار رغم التحذير" : t("draft.confirm")}
						</Button>
					)}
				</div>
			</div>
		</div>
	);
}
